import {syncTerrainRelief} from '../lib/terrain-relief.js';
import {SICILY_BOUNDS, isMobile} from '../lib/config.js';
import {
    CAMERA_STORAGE_KEY, clampPitch, normalizeBearing, readCameraState,
    setCameraMode, cameraSnapshot, attachCameraController,
} from '../lib/camera-state.js';

const DEM = 'ws-terrain-dem';
const LOCAL_DEM = 'ws-terrain-sitr';
const ELEVATION = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
const PLACES = {
    etna: {center: [15.004, 37.755], zoom: 11.4, pitch: 60, bearing: 22},
    madonie: {center: [14.037, 37.888], zoom: 11.1, pitch: 58, bearing: -18},
    nebrodi: {center: [14.651, 37.937], zoom: 10.8, pitch: 58, bearing: 12},
};

// A camera controller on the existing Maps instance. Weather sources, filters
// and the time controller continue to own their state independently.
export function initPerspective(map, urlState) {
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(CAMERA_STORAGE_KEY) || 'null'); } catch {}
    const initial = readCameraState(urlState.raw, stored);
    if (isMobile() && initial.mode === '3d' && !urlState.raw.has('pitch') && (urlState.raw.toString() || !stored)) {
        initial.pitch = 32;
        if (!urlState.raw.has('bearing')) initial.bearing = 0;
    }
    let mode = '2d';
    let last3D = {
        pitch: clampPitch(stored?.pitch ?? (initial.mode === '3d' ? initial.pitch : isMobile() ? 32 : 48)),
        bearing: normalizeBearing(stored?.bearing ?? initial.bearing),
    };
    let terrainSource = null;
    let terrainOn = false, failed = false, dead = false, syncFrame = 0;
    const duration = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650;
    const root = document.createElement('section');
    root.className = 'wsv2-perspective';
    root.setAttribute('aria-label', 'Prospettiva della mappa');
    root.innerHTML = `<div class="wsv2-perspective__modes" role="group" aria-label="Vista della mappa">
      <button type="button" id="ws-view-2d" aria-pressed="true" aria-label="Vista 2D">2D</button>
      <button type="button" id="ws-view-3d" aria-pressed="false" aria-label="Vista 3D">3D</button>
      <button type="button" id="ws-camera-options" aria-label="Controlli prospettiva" aria-controls="ws-camera-panel" aria-expanded="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 8 8-4 8 4-8 4-8-4Zm0 4 8 4 8-4M4 16l8 4 8-4"/></svg></button>
    </div><section id="ws-camera-panel" class="wsv2-perspective__panel" aria-label="Controlli prospettiva" hidden>
      <header><b>Prospettiva</b><button type="button" id="ws-camera-close" aria-label="Chiudi controlli prospettiva">×</button></header>
      <p id="ws-camera-status" role="status"></p>
      <label for="ws-camera-pitch">Inclinazione <output id="ws-camera-pitch-value">48°</output></label>
      <input id="ws-camera-pitch" type="range" min="0" max="65" step="1" value="48" aria-label="Inclinazione della mappa">
      <label for="ws-camera-bearing">Orientamento <output id="ws-camera-bearing-value">Nord</output></label>
      <input id="ws-camera-bearing" type="range" min="-180" max="180" step="1" value="0" aria-label="Orientamento della mappa">
      <div class="wsv2-perspective__actions"><button type="button" id="ws-camera-north">↑ Nord</button><button type="button" id="ws-camera-home">Sicilia</button></div>
      <div id="ws-camera-places" class="wsv2-perspective__places"><span>Esplora il rilievo</span><div>
        <button type="button" id="ws-camera-etna">Etna</button><button type="button" id="ws-camera-madonie">Madonie</button><button type="button" id="ws-camera-nebrodi">Nebrodi</button>
      </div></div>
      <button type="button" id="ws-camera-retry" hidden>Ricarica rilievo</button>
      <p class="wsv2-perspective__hint">Trascina con due dita per inclinare e ruotare. Su desktop usa il tasto destro.</p>
    </section>`;
    document.body.appendChild(root);
    const find = id => root.querySelector('#' + id);
    const panel = find('ws-camera-panel'), options = find('ws-camera-options');
    const flat = find('ws-view-2d'), three = find('ws-view-3d');
    const pitch = find('ws-camera-pitch'), bearing = find('ws-camera-bearing');
    const retry = find('ws-camera-retry');

    function panelOpen(on) {
        panel.hidden = !on;
        options.setAttribute('aria-expanded', String(on));
        if (on) sync();
        if (!on && panel.contains(document.activeElement)) options.focus();
    }
    function save() {
        if (dead || document.documentElement.classList.contains('wsv2-embed')) return;
        try {
            const camera = cameraSnapshot(map);
            localStorage.setItem(CAMERA_STORAGE_KEY, JSON.stringify(mode === '2d' ? {...camera, ...last3D} : camera));
        } catch {}
    }
    const syncValues = new Map();
    function attr(el, name, value) {
        if (el.getAttribute(name) !== value) el.setAttribute(name, value);
    }
    function text(id, value) {
        if (syncValues.get(id) === value) return;
        find(id).textContent = value;
        syncValues.set(id, value);
    }
    function sync() {
        attr(flat, 'aria-pressed', String(mode === '2d'));
        attr(three, 'aria-pressed', String(mode === '3d'));
        document.documentElement.classList.toggle('wsv2-view-3d', mode === '3d');
        if (root.dataset.mode !== mode) root.dataset.mode = mode;
        if (panel.hidden) return;
        pitch.disabled = bearing.disabled = mode !== '3d';
        const pv = String(Math.round(map.getPitch()));
        if (pitch.value !== pv) pitch.value = pv;
        const bv = String(Math.round(normalizeBearing(map.getBearing())));
        if (bearing.value !== bv) bearing.value = bv;
        text('ws-camera-pitch-value', `${pitch.value}°`);
        const b = Number(bearing.value);
        const orientation = Math.abs(b) < 1 ? 'Nord' : `${Math.abs(b)}° ${b < 0 ? 'O' : 'E'}`;
        text('ws-camera-bearing-value', orientation);
        attr(pitch, 'aria-valuetext', `${pitch.value} gradi`);
        attr(bearing, 'aria-valuetext', orientation);
        const loading = terrainOn && map.isSourceLoaded && !map.isSourceLoaded(terrainSource || DEM);
        text('ws-camera-status', mode === '2d' ? 'Vista zenitale'
            : failed ? 'Rilievo temporaneamente non disponibile'
            : map.getZoom() < 5 ? 'Prospettiva globale'
            : loading ? 'Caricamento rilievo…' : 'Rilievo reale');
        retry.hidden = !failed || mode !== '3d';
        find('ws-camera-places').hidden = mode !== '3d';
        root.querySelector('.wsv2-perspective__hint').hidden = mode !== '3d';
    }
    function gestures(on) {
        for (const handler of [map.dragRotate, map.touchPitch]) handler?.[on ? 'enable' : 'disable']();
        map.touchZoomRotate?.[on ? 'enableRotation' : 'disableRotation']();
        map.keyboard?.[on ? 'enableRotation' : 'disableRotation']();
    }
    function terrain() {
        const wanted = mode === '3d' && map.getZoom() >= 5 && !failed;
        const c = map.getCenter();
        const regional = map.getZoom() >= 6 && c.lng >= 11.8 && c.lng <= 15.8 && c.lat >= 35.4 && c.lat <= 38.9;
        const source = regional ? LOCAL_DEM : DEM;
        if (wanted === terrainOn && (!wanted || terrainSource === source)) return;
        try {
            if (wanted && !regional && !map.getSource(DEM)) {
                map.addSource(DEM, {
                    type: 'raster-dem', tiles: [ELEVATION], tileSize: 256,
                    maxzoom: 14, encoding: 'terrarium', attribution: 'Elevation © Mapzen / Tilezen',
                });
            }
            if (wanted && regional && !map.getSource(LOCAL_DEM)) {
                map.addSource(LOCAL_DEM, {type: 'raster-dem', tiles: [location.origin + '/assets/terrain-sitr/tile.php?z={z}&x={x}&y={y}'], tileSize: 256, minzoom: 6, maxzoom: 16, encoding: 'mapbox', bounds: [11.8,35.4,15.8,38.9], attribution: 'Regione Siciliana SITR · MDT LiDAR 2013, 2 m; integrazione Mapzen'});
            }
            map.setTerrain(wanted ? {source, exaggeration: 1} : null);
            syncTerrainRelief(map, source, wanted);
            terrainSource = wanted ? source : null;
            terrainOn = wanted;
        } catch {
            failed = true;
            terrainOn = false;
            map.setTerrain(null);
            syncTerrainRelief(map, source, false);
        }
    }
    function changed() {
        document.dispatchEvent(new CustomEvent('wsv2:viewchange', {detail: {map, mode, camera: cameraSnapshot(map)}}));
    }
    function setMode(next, {camera = null, animate = true, persist = true} = {}) {
        next = next === '3d' ? '3d' : '2d';
        if (next === mode && !camera) return;
        // A rapid toggle must remember the target perspective, rather than an
        // intermediate animation frame. Manual gestures commit at moveend.
        if (mode === '3d' && !map.isEasing?.()) {
            last3D = {pitch: clampPitch(map.getPitch()), bearing: normalizeBearing(map.getBearing())};
        }
        mode = next;
        setCameraMode(map, mode);
        gestures(mode === '3d');
        terrain();
        const target = mode === '3d'
            ? {pitch: clampPitch(camera?.pitch ?? last3D.pitch), bearing: normalizeBearing(camera?.bearing ?? last3D.bearing)}
            : {pitch: 0, bearing: 0};
        if (mode === '3d') last3D = target;
        map.easeTo({...target, duration: animate ? duration() : 0});
        sync();
        changed();
        if (persist) save();
    }
    function fitSicily(animate = true, camera = null) {
        const mobile = isMobile();
        const target = {
            pitch: mode === '3d' ? (camera?.pitch ?? (mobile ? 32 : 48)) : 0,
            bearing: mode === '3d' ? (camera?.bearing ?? (mobile ? 0 : -12)) : 0,
        };
        if (mode === '3d') last3D = target;
        map.fitBounds(SICILY_BOUNDS, {
            padding: mobile ? {top: 185, right: 20, bottom: 245, left: 20} : {top: 90, right: 270, bottom: 155, left: 45},
            ...target, duration: animate ? duration() : 0, retainPadding: false,
        });
    }
    flat.addEventListener('click', () => setMode('2d'));
    three.addEventListener('click', () => setMode('3d'));
    options.addEventListener('click', () => panelOpen(panel.hidden));
    find('ws-camera-close').addEventListener('click', () => panelOpen(false));
    pitch.addEventListener('input', () => {map.jumpTo({pitch: Number(pitch.value)}); sync();});
    bearing.addEventListener('input', () => {map.jumpTo({bearing: Number(bearing.value)}); sync();});
    find('ws-camera-north').addEventListener('click', () => map.easeTo({bearing: 0, duration: duration()}));
    find('ws-camera-home').addEventListener('click', () => fitSicily());
    for (const [id, camera] of Object.entries(PLACES)) {
        find('ws-camera-' + id).addEventListener('click', () => {
            setMode('3d', {camera, animate: false});
            map.easeTo({...camera, duration: duration(), padding: {top: 0, right: 0, bottom: 0, left: 0}});
            panelOpen(false);
        });
    }
    retry.addEventListener('click', () => {
        map.setTerrain(null);
        terrainOn = false;
        for (const id of [DEM, LOCAL_DEM]) if (map.getSource(id)) map.removeSource(id);
        failed = false;
        terrain();
        sync();
    });
    const outside = e => {if (!panel.hidden && !root.contains(e.target)) panelOpen(false);};
    const escape = e => {if (e.key === 'Escape') panelOpen(false);};
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    const onMove = () => {
        if (panel.hidden) return;
        if (syncFrame) return;
        syncFrame = requestAnimationFrame(() => {syncFrame = 0; if (!dead) sync();});
    };
    const onMoveEnd = () => {
        if (mode === '3d') last3D = {pitch: clampPitch(map.getPitch()), bearing: normalizeBearing(map.getBearing())};
        terrain();
        sync();
        save();
    };
    const onError = e => {
        if (mode !== '3d' || failed || (e.sourceId !== DEM && e.sourceId !== LOCAL_DEM && !String(e.error?.message || '').includes('elevation-tiles-prod'))) return;
        failed = true;
        map.setTerrain(null);
        terrainOn = false;
        syncTerrainRelief(map, terrainSource, false);
        sync();
        changed();
    };
    map.on('move', onMove);
    map.on('moveend', onMoveEnd);
    map.on('error', onError);
    map.on('sourcedata', e => {if ((e.sourceId === DEM || e.sourceId === LOCAL_DEM) && !dead) onMove();});
    const controller = {setMode, fitSicily, getSnapshot: () => cameraSnapshot(map)};
    attachCameraController(map, controller);
    if (window.wsv2) window.wsv2.camera = controller;
    setMode(initial.mode, {camera: initial, animate: false, persist: false});
    const focused = urlState.lat != null || ['station', 'stazione', 'view', 'province', 'comune', 'municipality', 'city'].some(k => urlState.raw.has(k));
    if (initial.mode === '3d' && !focused) fitSicily(false, initial);
    map.once('remove', () => {
        dead = true;
        cancelAnimationFrame(syncFrame);
        document.removeEventListener('pointerdown', outside);
        document.removeEventListener('keydown', escape);
        root.remove();
    });
    return controller;
}
